import { ComponentProps, forwardRef } from 'react'
import { twMerge } from 'tailwind-merge'
import { isSafari } from 'shared/utils/is-safari'

interface ScrollSafeIframeProps extends ComponentProps<'iframe'> {}

/**
 * Sets the wrapper height so the lecture iframe can remain 100% height.
 * Required to avoid a cross-browser iframe scroll bug (Shortcut 87753).
 * Use this helper for all lecture iframe height updates.
 */
export const setScrollSafeIframeWrapperHeight = (iframe: HTMLIFrameElement, height?: number) => {
  const wrapper = iframe.parentElement
  if (!wrapper) return

  wrapper.style.height = height ? `${height}px` : '100%'
}

// We need to use this component because it helps solve the issue with Safari scroll. In my opinion, we should get rid of the iframe
export const ScrollSafeIframe = forwardRef<HTMLIFrameElement, ScrollSafeIframeProps>(
  function ScrollSafeIframe({ srcDoc, ...props }, ref) {
    return (
      // Wrapper required: height is applied here by setScrollSafeIframeWrapperHeight
      // to avoid a cross-browser iframe scroll bug (Shortcut 87753). Do not remove.
      <div className="relative w-full">
        <iframe
          ref={ref}
          className={twMerge(
            'hide-scrollbar absolute size-full opacity-0',
            isSafari() ? 'max-h-[85%] overflow-y-auto' : 'overflow-hidden',
          )}
          data-test-id="course-lecture-iframe"
          srcDoc={srcDoc}
          {...props}
        />
      </div>
    )
  },
)
